// import store from '../store/store';

// List of data keys that should be persisted
const PERSISTENT_DATA_KEYS = [
  'auth',
  'users',
  'roles',
  'dashboard', 
  'polls',
  'townhall',
  'manifesto',
  'media',
  'event',
  'funds',
  'expense',
  'account',
  // Add other data types you want to persist
];

// Add a new function to immediately save auth state
export const saveAuthStateImmediately = async (authState) => {
  if (!window.electronAPI) return false;
  
  // Use the provided authState instead of accessing the store
  if (authState) {
    try {
      
      await window.electronAPI.writeFile(
        'auth.json', 
        JSON.stringify(authState)
      );
      
      const manifestExists = await window.electronAPI.fileExists('data_manifest.json');
      let manifest = {
        lastUpdated: new Date().toISOString(),
        dataKeys: PERSISTENT_DATA_KEYS
      };
      
      if (manifestExists) {
        const manifestJson = await window.electronAPI.readFile('data_manifest.json');
        if (manifestJson) {
          try {
            const existingManifest = JSON.parse(manifestJson);
            manifest = {
              ...existingManifest,
              lastUpdated: new Date().toISOString()
            };
          } catch (error) {
            console.error('Error parsing existing manifest:', error);
          }
        }
      }
      
      await window.electronAPI.writeFile('data_manifest.json', JSON.stringify(manifest));
      
      return true;
    } catch (error) {
      console.error('Failed to save auth state immediately:', error);
      return false;
    }
  }
  
  return false;
};

export const saveDataToStorage = async (state) => {
  if (!window.electronAPI) return false;
  
  // Use the provided state or get current state from store
  // const state = store.getState();
  
  // Save each data type in its own file
  for (const key of PERSISTENT_DATA_KEYS) {
    if (state[key]) {
      try {
        await window.electronAPI.writeFile(
          `${key}.json`, 
          JSON.stringify(state[key])
        );
      } catch (error) {
        console.error(`Failed to save ${key} data:`, error);
      }
    }
  }
  
  // Also save a manifest file with timestamp for freshness check
  const manifest = {
    lastUpdated: new Date().toISOString(),
    dataKeys: PERSISTENT_DATA_KEYS
  };
  
  await window.electronAPI.writeFile('data_manifest.json', JSON.stringify(manifest));
  
  return true;
};

if (typeof window !== 'undefined') {
  window.saveAppDataBeforeClose = async () => {
    // Get the store dynamically to avoid circular dependency
    const store = await import('../store/store').then(module => module.default);
    await saveDataToStorage(store.getState());
  };
}

// Setup periodic saving function
export const setupPeriodicSaving = () => {
  // Save every 5 minutes
  const saveInterval = setInterval(async () => {
    // Get the store dynamically to avoid circular dependency
    const store = await import('../store/store').then(module => module.default);
    saveDataToStorage(store.getState());
  }, 5 * 60 * 1000);
  
  return () => clearInterval(saveInterval);
};

export const loadDataFromStorage = async () => {
  if (!window.electronAPI) return null;
  
  // Check if manifest exists
  const manifestExists = await window.electronAPI.fileExists('data_manifest.json');
  if (!manifestExists) {
    return null;
  }
  
  // Load manifest
  const manifestJson = await window.electronAPI.readFile('data_manifest.json');
  if (!manifestJson) return null;
  
  const manifest = JSON.parse(manifestJson);
  
  // Load each data file
  const loadedData = {};
  
  for (const key of manifest.dataKeys) {
    const dataExists = await window.electronAPI.fileExists(`${key}.json`);
    if (dataExists) {
      const dataJson = await window.electronAPI.readFile(`${key}.json`);
      if (dataJson) {
        try {
          loadedData[key] = JSON.parse(dataJson);
        } catch (error) {
          console.error(`Failed to parse ${key} data:`, error);
        }
      }
    }
  }
  
  return loadedData;
};

export const clearAllStoredData = async () => {
  if (!window.electronAPI) return false;
  
  try {
    // Check if manifest exists to get the list of files
    const manifestExists = await window.electronAPI.fileExists('data_manifest.json');
    
    if (manifestExists) {
      const manifestJson = await window.electronAPI.readFile('data_manifest.json');
      if (manifestJson) {
        const manifest = JSON.parse(manifestJson);
        
        for (const key of manifest.dataKeys) {
          try {
            await window.electronAPI.deleteFile(`${key}.json`);
          } catch (err) {
            console.error(`Failed to delete ${key}.json:`, err);
          }
        }
      }
      
      await window.electronAPI.deleteFile('data_manifest.json');
    }
    
    return true;
  } catch (error) {
    console.error('Failed to clear persistent storage:', error);
    return false;
  }
};